/**
 * ─── POS Entity Definitions ──────────────────────────────────
 * Self-registers all POS entities with the Entity Registry on import.
 *
 * Importing '@repo/entity-pos' wires up:
 *  - Navigation menu items (under "POS" group)
 *  - Sync configuration per entity
 *  - RBAC permission prefixes
 *  - Audit trail settings
 *  - Tenant isolation
 */

import { EntityRegistry } from '@repo/core'
import type { EntityDefinition } from '@repo/core'
import type {
  POSCategory,
  POSProduct,
  POSSale,
  POSSaleItem,
  POSShift,
} from './pos.schema'
import { POSProductHooks, POSSaleHooks, POSShiftHooks } from './pos.hooks'

// ─── Category Entity ──────────────────────────────────────

export const POSCategoryEntity: EntityDefinition<POSCategory> = {
  name: 'pos_categories',
  ui: {
    label: 'Category',
    labelPlural: 'Categories',
    icon: 'FolderTree',
    routePath: 'pos/categories',
    color: 'blue',
    showInNav: true,
    navOrder: 10,
    navGroup: 'POS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'background' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pos_category' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Product Entity ───────────────────────────────────────

export const POSProductEntity: EntityDefinition<POSProduct> = {
  name: 'pos_products',
  ui: {
    label: 'Product',
    labelPlural: 'Products',
    icon: 'Package',
    routePath: 'pos/products',
    color: 'purple',
    showInNav: true,
    navOrder: 20,
    navGroup: 'POS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pos_product' },
  hooks: POSProductHooks,
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Sale Entity ──────────────────────────────────────────

export const POSSaleEntity: EntityDefinition<POSSale> = {
  name: 'pos_sales',
  ui: {
    label: 'Sale',
    labelPlural: 'Sales',
    icon: 'Receipt',
    routePath: 'pos/sales',
    color: 'green',
    showInNav: true,
    navOrder: 30,
    navGroup: 'POS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pos_sale' },
  hooks: POSSaleHooks,
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

// ─── Sale Item Entity ─────────────────────────────────────

export const POSSaleItemEntity: EntityDefinition<POSSaleItem> = {
  name: 'pos_sale_items',
  ui: {
    label: 'Sale Item',
    labelPlural: 'Sale Items',
    icon: 'List',
    routePath: 'pos/sale-items',
    color: 'yellow',
    showInNav: false, // Accessed from within sales
    navOrder: 35,
    navGroup: 'POS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'pos_sale' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

// ─── Shift Entity ─────────────────────────────────────────

export const POSShiftEntity: EntityDefinition<POSShift> = {
  name: 'pos_shifts',
  ui: {
    label: 'Shift',
    labelPlural: 'Shifts',
    icon: 'Clock',
    routePath: 'pos/shifts',
    color: 'yellow',
    showInNav: true,
    navOrder: 40,
    navGroup: 'POS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'pos_shift' },
  hooks: POSShiftHooks,
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

// ─── Self-Register All ────────────────────────────────────

EntityRegistry.register(POSCategoryEntity)
EntityRegistry.register(POSProductEntity)
EntityRegistry.register(POSSaleEntity)
EntityRegistry.register(POSSaleItemEntity)
EntityRegistry.register(POSShiftEntity)
